import React from 'react';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import {
  standardColours,
  minBreakpointQuery,
  sectionMargins,
  fontWeights,
  fontSize,
  brandColours,
} from '../styles';
import { Container, HtmlContent } from './ui';
import EventSidebar from './EventSidebar';
import linkIcon from '../images/link-icon.svg';

const StyledEventDetail = styled.article`
  ${sectionMargins('25px', '50px')};
`;

const StyledInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${minBreakpointQuery.mlarge`
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const StyledMainContent = styled.section`
  width: 100%;
  display: grid;
  gap: 40px;

  ${minBreakpointQuery.mlarge`
    width: 600px;
  `};
`;

const StyledContent = styled(HtmlContent)`
  h2 {
    color: ${brandColours.primary};
    font-weight: ${fontWeights.regular};
  }
`;

const StyledVenue = styled.div`
  background-color: ${standardColours.lightestGrey};
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${minBreakpointQuery.small`
    padding: 35px;
  `};

  ${minBreakpointQuery.mlarge`
    height: max-content;  
    flex-direction: row;
    gap: 90px;
    padding: 45px;
  `};
`;

const StyledAddress = styled.div``;

const StyledSubHeading = styled.h3`
  font-weight: ${fontWeights.semibold};
  ${fontSize(16)};
  margin-bottom: 4px;
`;

const StyledContact = styled.div`
  flex-shrink: 0;
`;

const StyledLink = styled.a`
  display: block;
  margin-top: 22px;
  color: ${brandColours.primary};
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;

  &:after {
    content: '';
    background: url(${linkIcon}) no-repeat;
    font-size: 0;
    height: 18px;
    width: 18px;
    display: block;
    margin-left: 14px;
  }
`;

const StyledMapWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding-top: 56.25%;
`;

const StyledMap = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const StyledSidebar = styled(EventSidebar)`
  ${minBreakpointQuery.smedium`
    position: sticky;
    top: 20px; 
    height: fit-content; 
  `}

  ${minBreakpointQuery.mlarge`
    width: 380px;
  `}
`;

const StyledInfo = styled.div`
  padding: 25px;
  background-color: ${standardColours.lightestGrey};
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  ${minBreakpointQuery.small`
    padding: 35px;
    flex-direction: row;
  `};

  ${minBreakpointQuery.smedium`
    padding: 45px;
  `};

  ${minBreakpointQuery.mlarge`
    flex-direction: column;
  `}
`;

const StyledCategoryHeading = styled.h3`
  font-weight: ${fontWeights.semibold};
  ${fontSize(16)};
  margin-bottom: 4px;
  margin-top: 30px;

  ${minBreakpointQuery.small`
    margin-top: 0;
  `};

  ${minBreakpointQuery.mlarge`
    margin-top: 30px;
  `};
`;

const StyledCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;

  ${minBreakpointQuery.mlarge`
    max-width: 300px;
  `};
`;

const StyledCategoryName = styled.span`
  max-width: fit-content;
  ${fontSize(12)};
  background-color: ${standardColours.lighterGrey};
  padding: 6px 12px;
  cursor: pointer;
`;

const EventDetail = ({
  content,
  venue: { name, address, phoneNumber, venueWebsite },
  cta,
  startDateTime,
  endDateTime,
  categories,
  website,
}) => {
  const mapAddress = address
    ? encodeURIComponent(
        `${name} + ${address.replaceAll(',', '+').replaceAll(/\s/g, '')}`,
      ).replaceAll('%2B', '+')
    : false;

  return (
    <StyledEventDetail>
      <Container>
        <StyledInner>
          <StyledMainContent>
            <StyledContent content={content} isEventDetail={true} />
            {(address || phoneNumber || venueWebsite) && (
              <StyledVenue>
                {address && (
                  <StyledAddress>
                    <StyledSubHeading>Venue Address</StyledSubHeading>
                    <p>{address}</p>
                  </StyledAddress>
                )}
                {(phoneNumber || venueWebsite) && (
                  <StyledContact>
                    {phoneNumber && (
                      <>
                        <StyledSubHeading>Venue Phone Number</StyledSubHeading>
                        <p>{phoneNumber}</p>
                      </>
                    )}
                    {venueWebsite && (
                      <StyledLink
                        href={venueWebsite}
                        target="_blank"
                        rel="noopener"
                      >
                        Visit venue website
                      </StyledLink>
                    )}
                  </StyledContact>
                )}
              </StyledVenue>
            )}
            {mapAddress && (
              <StyledMapWrapper>
                <StyledMap
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.GATSBY_GOOGLE_MAP_API}&q=${mapAddress}`}
                  loading="lazy"
                ></StyledMap>
              </StyledMapWrapper>
            )}
          </StyledMainContent>
          <StyledSidebar cta={cta}>
            <StyledInfo>
              <div>
                <StyledSubHeading>Start</StyledSubHeading>
                <span>{startDateTime}</span>
                <br />
                <br />
                <StyledSubHeading>End</StyledSubHeading>
                <span>{endDateTime}</span>
              </div>
              <div>
                <StyledCategoryHeading>
                  Trade Show Categories
                </StyledCategoryHeading>
                <StyledCategories>
                  {categories.map(({ title }, id) => (
                    <StyledCategoryName
                      key={id}
                      onClick={() =>
                        navigate('/exhibition-calendar', {
                          state: { event: title },
                        })
                      }
                    >
                      {title}
                    </StyledCategoryName>
                  ))}
                </StyledCategories>
                <StyledLink href={website} target="_blank" rel="noopener">
                  Visit event website
                </StyledLink>
              </div>
            </StyledInfo>
          </StyledSidebar>
        </StyledInner>
      </Container>
    </StyledEventDetail>
  );
};

export default EventDetail;
